/**
 * Markdown Library - Formatters
 * 
 * Pure functions for formatting and transforming content.
 * All functions are stateless and easily testable.
 */

import katex from 'katex';
import type { CellFormatResult, CellType } from './types';
import {
    BRAZILIAN_CURRENCY_PATTERN,
    BRAZILIAN_CURRENCY_INTEGER_WITH_THOUSANDS,
    BRAZILIAN_NUMBER_WITH_THOUSANDS,
    BRAZILIAN_INTEGER_WITH_THOUSANDS,
    BRAZILIAN_DECIMAL,
    INTEGER_PATTERN,
    PERCENTAGE_WITH_THOUSANDS,
    PERCENTAGE_INTEGER_WITH_THOUSANDS,
    SIMPLE_PERCENTAGE,
    NUMERIC_HEADER_KEYWORDS,
} from './patterns';

// =============================================================================
// Cell Content Detection & Formatting
// =============================================================================

/**
 * Detects the type of content in a table cell and returns formatted value.
 * 
 * @param content - Raw cell content
 * @returns Object with formatted string and detected type
 */
export function formatCellContent(content: string): CellFormatResult {
    const trimmed = content.trim();
    // Strip inline markdown (bold/italic) before type detection so that **4,16** is
    // correctly classified as a number even when the agent bolds a cell value.
    const stripped = trimmed
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '$1')
        .replace(/(?<!_)_([^_]+)_(?!_)/g, '$1')
        .trim();

    // Check for Brazilian currency (R$ prefix or implied)
    if (BRAZILIAN_CURRENCY_PATTERN.test(stripped) || BRAZILIAN_CURRENCY_INTEGER_WITH_THOUSANDS.test(stripped) || BRAZILIAN_NUMBER_WITH_THOUSANDS.test(stripped)) {
        return { formatted: trimmed, type: 'currency' };
    }

    // Check for percentage
    if (PERCENTAGE_WITH_THOUSANDS.test(stripped) || PERCENTAGE_INTEGER_WITH_THOUSANDS.test(stripped) || SIMPLE_PERCENTAGE.test(stripped)) {
        return { formatted: trimmed, type: 'percentage' };
    }

    // Check for numbers (Brazilian format)
    if (BRAZILIAN_DECIMAL.test(stripped) || INTEGER_PATTERN.test(stripped) || BRAZILIAN_INTEGER_WITH_THOUSANDS.test(stripped)) {
        return { formatted: trimmed, type: 'number' };
    }

    return { formatted: trimmed, type: 'text' };
}

/**
 * Checks if a header name indicates numeric content.
 * Used to determine column alignment.
 * 
 * @param header - Header text
 * @returns True if the header suggests numeric content
 */
export function isNumericHeader(header: string): boolean {
    const lower = header.toLowerCase().trim();
    return NUMERIC_HEADER_KEYWORDS.some(keyword => lower.includes(keyword));
}

// =============================================================================
// Markdown Processing
// =============================================================================

/** Escape text for safe HTML body content. */
function escapeHtmlBody(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Escape a URL for use inside a double-quoted HTML attribute. */
function escapeHtmlAttrValue(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;');
}

function isSafeHttpHref(href: string): boolean {
    const t = href.trim();
    if (!/^https?:\/\//i.test(t)) return false;
    if (/^https?:\/\/\s*javascript:/i.test(t)) return false;
    return true;
}

/**
 * Parses a CommonMark-style inline link starting at text[start] === '['.
 * Supports nested brackets in the label (e.g. [[5]](https://...)).
 */
function parseMarkdownInlineLink(
    text: string,
    start: number
): { linkText: string; href: string; end: number } | null {
    if (text[start] !== '[') return null;
    let i = start + 1;
    let depth = 1;
    while (i < text.length) {
        const c = text[i];
        if (c === '\\' && i + 1 < text.length) {
            i += 2;
            continue;
        }
        if (c === '[') {
            depth += 1;
            i += 1;
            continue;
        }
        if (c === ']') {
            depth -= 1;
            if (depth === 0) {
                if (i + 1 < text.length && text[i + 1] === '(') {
                    const linkText = text.slice(start + 1, i);
                    let j = i + 2;
                    let href = '';
                    while (j < text.length) {
                        if (text[j] === '\\' && j + 1 < text.length) {
                            href += text[j] + text[j + 1];
                            j += 2;
                            continue;
                        }
                        if (text[j] === ')') {
                            // CommonMark allows angle-bracket-wrapped URLs: [text](<url>)
                            const cleanHref = href.startsWith('<') && href.endsWith('>') ? href.slice(1, -1) : href;
                            return { linkText, href: cleanHref, end: j + 1 };
                        }
                        href += text[j];
                        j += 1;
                    }
                    return null;
                }
                return null;
            }
            i += 1;
            continue;
        }
        i += 1;
    }
    return null;
}

function isNumericCitationLink(href: string, linkText: string): boolean {
    if (!/^\d+$/.test(linkText.trim())) return false;
    const h = href.toLowerCase();
    return (
        h.includes('vertexaisearch.cloud.google.com') ||
        h.includes('grounding-api-redirect') ||
        (h.includes('google.com/search') && h.includes('q='))
    );
}

/**
 * Strips markdown formatting from text.
 * Useful for exporting clean data to Excel.
 * 
 * @param text - Text with markdown formatting
 * @returns Clean text without markdown
 */
export function stripMarkdown(text: string): string {
    return text
        .replace(/\*\*/g, '')  // Bold
        .replace(/\*/g, '')    // Italic
        .replace(/`/g, '')     // Code
        .replace(/~~/g, '')    // Strikethrough
        .replace(/__/g, '')    // Alt bold
        .replace(/_/g, '')     // Alt italic
        .trim();
}

/**
 * Converts inline markdown to HTML.
 * Handles: **bold**, *italic*, `code`, ~~strikethrough~~, [label](url) links
 *
 * IMPORTANT: link extraction must happen on the raw text BEFORE HTML escaping.
 * escapeHtmlBody converts < > to &lt; &gt;, which breaks CommonMark angle-bracket
 * URLs like [n](<https://...>) — the parser would see &lt;https://...&gt; and
 * isSafeHttpHref would fail. Extracting first avoids this.
 */
export function processInlineMarkdown(text: string): string {
    // Step 1 — extract markdown links from raw text into placeholders.
    const linkPlaceholders: string[] = [];
    let rawWithTokens = '';
    let ci = 0;
    while (ci < text.length) {
        if (text[ci] === '[') {
            const link = parseMarkdownInlineLink(text, ci);
            if (link && isSafeHttpHref(link.href)) {
                const safeHref = escapeHtmlAttrValue(link.href.trim());
                const safeLabel = escapeHtmlBody(link.linkText);
                const isCitation = isNumericCitationLink(link.href, link.linkText);
                const displayLabel = isCitation ? `[${safeLabel}]` : safeLabel;
                const cls = isCitation
                    ? 'inline-block mx-0.5 whitespace-nowrap text-teal-700 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 underline font-medium text-sm align-baseline'
                    : 'text-blue-600 dark:text-blue-400 hover:underline';
                const html = `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="${cls}">${displayLabel}</a>`;
                linkPlaceholders.push(html);
                rawWithTokens += `\x00L${linkPlaceholders.length - 1}\x00`;
                ci = link.end;
                continue;
            }
        }
        rawWithTokens += text[ci];
        ci += 1;
    }

    // Step 1.5 — typeset inline math ($...$ with a math token or an identifier body)
    // into KaTeX HTML placeholders BEFORE HTML-escaping. Without this, table cells
    // strip the "$" and show raw LaTeX like "\frac{0,05}{1}". Currency ("R$ 140,00")
    // has no closing "$" with a math body, so it is left untouched.
    const mathPlaceholders: string[] = [];
    // Display math ($$...$$) first, mirroring the guard in escapeCurrencyForLatex:
    // parsing single-$ before $$ would misalign on a "$$expr$$" span and leave a
    // dangling $ that could corrupt whatever comes after it. Table cells are
    // single-line, so this mainly protects a "$$expr$$" written inline in a cell.
    rawWithTokens = rawWithTokens.replace(/(?<!\\)\$\$([\s\S]*?)\$\$/g, (_, body: string) => {
        let html: string;
        try {
            html = katex.renderToString(body.trim(), { throwOnError: false, displayMode: true });
        } catch {
            html = escapeHtmlBody(body);
        }
        mathPlaceholders.push(html);
        return `\x00K${mathPlaceholders.length - 1}\x00`;
    });
    rawWithTokens = rawWithTokens.replace(
        // Bare-identifier body uses [ \t]* (not \s*) so a stray unmatched $ can
        // only ever swallow same-line text, never cross a paragraph/line break.
        /(?<!\\)\$((?:[^$\n]*[\^_{}\\][^$\n]*)|(?:[ \t]*[A-Za-z][A-Za-z0-9]*[ \t]*))\$/g,
        (_, body: string) => {
            let html: string;
            try {
                html = katex.renderToString(body.trim(), { throwOnError: false, displayMode: false });
            } catch {
                html = escapeHtmlBody(body);
            }
            mathPlaceholders.push(html);
            return `\x00K${mathPlaceholders.length - 1}\x00`;
        }
    );

    // Step 2 — HTML-escape the non-link text. Tokens use \x00 which is not HTML-special.
    let result = escapeHtmlBody(rawWithTokens);

    // LaTeX symbol map - comprehensive list
    const latexSymbols: Record<string, string> = {
        // Arrows
        '\\rightarrow': '→',
        '\\leftarrow': '←',
        '\\leftrightarrow': '↔',
        '\\Rightarrow': '⇒',
        '\\Leftarrow': '⇐',
        '\\Leftrightarrow': '⇔',
        '\\uparrow': '↑',
        '\\downarrow': '↓',
        '\\updownarrow': '↕',
        '\\nearrow': '↗',
        '\\searrow': '↘',
        '\\nwarrow': '↖',
        '\\swarrow': '↙',
        '\\longrightarrow': '⟶',
        '\\longleftarrow': '⟵',
        '\\mapsto': '↦',

        // Greek letters (lowercase)
        '\\alpha': 'α',
        '\\beta': 'β',
        '\\gamma': 'γ',
        '\\delta': 'δ',
        '\\epsilon': 'ε',
        '\\zeta': 'ζ',
        '\\eta': 'η',
        '\\theta': 'θ',
        '\\iota': 'ι',
        '\\kappa': 'κ',
        '\\lambda': 'λ',
        '\\mu': 'μ',
        '\\nu': 'ν',
        '\\xi': 'ξ',
        '\\pi': 'π',
        '\\rho': 'ρ',
        '\\sigma': 'σ',
        '\\tau': 'τ',
        '\\upsilon': 'υ',
        '\\phi': 'φ',
        '\\chi': 'χ',
        '\\psi': 'ψ',
        '\\omega': 'ω',

        // Greek letters (uppercase)
        '\\Alpha': 'Α',
        '\\Beta': 'Β',
        '\\Gamma': 'Γ',
        '\\Delta': 'Δ',
        '\\Epsilon': 'Ε',
        '\\Zeta': 'Ζ',
        '\\Eta': 'Η',
        '\\Theta': 'Θ',
        '\\Iota': 'Ι',
        '\\Kappa': 'Κ',
        '\\Lambda': 'Λ',
        '\\Mu': 'Μ',
        '\\Nu': 'Ν',
        '\\Xi': 'Ξ',
        '\\Pi': 'Π',
        '\\Rho': 'Ρ',
        '\\Sigma': 'Σ',
        '\\Tau': 'Τ',
        '\\Upsilon': 'Υ',
        '\\Phi': 'Φ',
        '\\Chi': 'Χ',
        '\\Psi': 'Ψ',
        '\\Omega': 'Ω',

        // Math operators
        '\\times': '×',
        '\\div': '÷',
        '\\pm': '±',
        '\\mp': '∓',
        '\\cdot': '·',
        '\\ast': '∗',
        '\\star': '⋆',
        '\\circ': '∘',
        '\\bullet': '•',

        // Comparisons
        '\\neq': '≠',
        '\\leq': '≤',
        '\\geq': '≥',
        '\\le': '≤',
        '\\ge': '≥',
        '\\ll': '≪',
        '\\gg': '≫',
        '\\approx': '≈',
        '\\sim': '∼',
        '\\equiv': '≡',
        '\\propto': '∝',

        // Sets and logic
        '\\in': '∈',
        '\\notin': '∉',
        '\\subset': '⊂',
        '\\supset': '⊃',
        '\\subseteq': '⊆',
        '\\supseteq': '⊇',
        '\\cup': '∪',
        '\\cap': '∩',
        '\\emptyset': '∅',
        '\\exists': '∃',
        '\\forall': '∀',
        '\\neg': '¬',
        '\\land': '∧',
        '\\lor': '∨',

        // Special symbols
        '\\infty': '∞',
        '\\partial': '∂',
        '\\nabla': '∇',
        '\\sum': '∑',
        '\\prod': '∏',
        '\\int': '∫',
        '\\sqrt': '√',
        '\\checkmark': '✓',
        '\\dagger': '†',
        '\\ddagger': '‡',
        '\\degree': '°',
        '\\prime': '′',
        '\\ldots': '…',
        '\\cdots': '⋯',
    };

    // Replace LaTeX symbols - both with and without $ delimiters
    for (const [latex, unicode] of Object.entries(latexSymbols)) {
        // With $ delimiters: $\rightarrow$
        const withDelimiters = new RegExp(`\\$${latex.replace(/\\/g, '\\\\')}\\$`, 'g');
        result = result.replace(withDelimiters, unicode);

        // Without $ delimiters: \rightarrow (but not inside other words)
        const withoutDelimiters = new RegExp(`(?<![a-zA-Z])${latex.replace(/\\/g, '\\\\')}(?![a-zA-Z])`, 'g');
        result = result.replace(withoutDelimiters, unicode);
    }

    // Handle remaining inline LaTeX that wasn't matched (just remove the $ delimiters)
    result = result.replace(/\$([^$]+)\$/g, '$1');

    // Step 3 — bold/italic/code/strikethrough (links already safe in placeholders)
    // Bold: **text** or __text__
    result = result.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>');
    result = result.replace(/__([^_]+)__/g, '<strong class="font-semibold">$1</strong>');

    // Italic: *text* or _text_ (but not part of **)
    result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic">$1</em>');
    result = result.replace(/(?<!_)_([^_]+)_(?!_)/g, '<em class="italic">$1</em>');

    // Code: `code`
    result = result.replace(
        /`([^`]+)`/g,
        '<code class="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>'
    );

    // Strikethrough: ~~text~~
    result = result.replace(/~~([^~]+)~~/g, '<del class="line-through opacity-60">$1</del>');

    // Step 4 — restore link placeholders extracted in Step 1
    for (let pi = 0; pi < linkPlaceholders.length; pi++) {
        result = result.split(`\x00L${pi}\x00`).join(linkPlaceholders[pi]);
    }

    // Step 4.5 — restore KaTeX math HTML extracted in Step 1.5
    for (let mi = 0; mi < mathPlaceholders.length; mi++) {
        result = result.split(`\x00K${mi}\x00`).join(mathPlaceholders[mi]);
    }

    return result;
}



// =============================================================================
// Currency Symbol Escaping (for LaTeX compatibility)
// =============================================================================

/**
 * Escapes currency dollar signs to prevent KaTeX from treating them as math delimiters.
 *
 * Handles:
 *   - Currency codes followed by $: R$, US$, A$, C$, HK$, etc.  → prefix\$
 *   - Standalone $ immediately before a digit or whitespace+digit: $103, $ 50  → \$103
 *
 * Does NOT escape $ signs that are genuinely inside LaTeX expressions.
 *
 * @param text - Text potentially containing currency dollar signs
 * @returns Text with currency $ signs escaped for ReactMarkdown/KaTeX
 */
export function escapeCurrencyForLatex(text: string): string {
    // Normalize: un-escape any already-escaped currency $ (e.g., US\$ → US$, R\$ → R$)
    // so we can apply uniform escaping in one pass.
    const clean = text.replace(/([A-Za-z])\\+\$/g, '$1$');

    // Protect real inline-math spans from the currency escaper below. Two shapes:
    //   1. body has a math token (^ _ {} \\): $10^{15}$, $\\frac{a}{b}$, $x\\%$
    //   2. body is a bare identifier: $r$, $n$, $US$ — so a closing "$" preceded by
    //      a letter isn't mistaken for currency (which used to break it into "r\\").
    const mathSpans: string[] = [];

    // Pass A — protect DISPLAY math ($$...$$) FIRST, in its own pass that
    // understands the double-dollar delimiter and spans newlines ([\s\S]*?).
    // Without this, the single-$ pass below (which doesn't know about $$)
    // misparses "$$expr$$" as "$" (untouched) + "$expr$" (matched) + "$"
    // (dangling) — and that dangling $ then pairs with unrelated later text
    // (even swallowing a whole word like "Onde" across a paragraph break via
    // the bare-identifier alternative), corrupting a real span like $\\alpha$
    // further down the document.
    const guardedDisplay = clean.replace(/(?<!\\)\$\$([\s\S]*?)\$\$/g, (match) => {
        mathSpans.push(match);
        return `MATH${mathSpans.length - 1}`;
    });

    // Pass B — protect single-$ inline math. The bare-identifier alternative
    // uses [ \t]* (not \s*) so it can never span a newline/paragraph break —
    // a stray unmatched $ can only ever swallow a single line, not a whole
    // subsequent paragraph.
    const guarded = guardedDisplay.replace(/(?<!\\)\$(?:[^$\n]*[\^_{}\\][^$\n]*|[ \t]*[A-Za-z][A-Za-z0-9]*[ \t]*)\$/g, (match) => {
        mathSpans.push(match);
        return `MATH${mathSpans.length - 1}`;
    });

    // Now that real math is stashed, escape currency dollars:
    //   • letter-prefixed codes (R$, US$, A$) — escape the $ (handles "R$ e", no digit)
    //   • a bare amount ($50, $ 1.000) — escape the $ before the digits
    // (?<!\\) stops the second pass from double-escaping the first pass's output.
    const escaped = guarded
        .replace(/([A-Za-z]+)\$/g, (_, prefix) => `${prefix}\\$`)
        .replace(/(?<!\\)\$(?=\s*\d)/g, '\\$')
        // Orphan trailing "$" right after a number (e.g. the stray one left by a
        // malformed "$US$ 2.000$"). Escaping it stops that lone $ from pairing with
        // the next real math span and turning a whole paragraph into math.
        .replace(/(?<=\d)\$(?!\s*\d)/g, '\\$');

    // Restore the protected math spans untouched.
    return escaped.replace(/MATH(\d+)/g, (_, i) => mathSpans[Number(i)]);
}

// =============================================================================
// Alignment Utilities
// =============================================================================

/**
 * Gets CSS class for text alignment.
 * 
 * @param align - Alignment direction
 * @returns Tailwind CSS class
 */
export function getAlignmentClass(align: 'left' | 'center' | 'right'): string {
    const alignmentMap = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };
    return alignmentMap[align];
}

/**
 * Gets CSS classes for a cell based on its content type.
 * 
 * @param type - Detected cell type
 * @returns Tailwind CSS classes
 */
export function getCellTypeClasses(type: CellType): string {
    const baseClasses = 'text-sm';

    switch (type) {
        case 'currency':
            return `${baseClasses} font-mono font-medium text-accent text-right`;
        case 'percentage':
            return `${baseClasses} font-mono font-medium text-blue-600 dark:text-blue-400 text-right`;
        case 'number':
            return `${baseClasses} font-mono font-medium text-slate-700 dark:text-slate-300 text-right`;
        case 'text':
        default:
            return `${baseClasses} text-slate-700 dark:text-slate-300`;
    }
}
